import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc, collection, query, where, getDocs, orderBy, updateDoc, deleteDoc } from "firebase/firestore";
import TemoignagesEspace from "./TemoignagesEspace";

/**
 * ImmersionConfigTab — Onglet "Immersion" dans Ma Boîte à Outils
 * ----------------------------------------------------------------
 * Permet à chaque distributrice de remplir son propre parcours
 * d'immersion directement depuis l'app, sans toucher au code.
 *
 * PROPS :
 * - uid : identifiant de la distributrice connectée
 * - db  : instance Firestore déjà initialisée dans App.js
 * - isChef : si true, affiche en plus l'onglet "Contenu commun"
 *            (formations/outils partagés par toute l'équipe)
 */

const COLORS = { or: "#C9A55C", brun: "#3D2B1F", rose: "#E7C9C9", creme: "#FAF7F2" };

function Champ({ label, value, onChange, textarea, placeholder }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: COLORS.brun, marginBottom: 6 }}>
        {label}
      </label>
      {textarea ? (
        <textarea
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.15)", fontFamily: "inherit", resize: "vertical", boxSizing: "border-box" }}
        />
      ) : (
        <input
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.15)", fontFamily: "inherit", boxSizing: "border-box" }}
        />
      )}
    </div>
  );
}

const OUTILS_DEFAUT_COMMUN = [
  {
    titre: "L'app Blazing Dynasty",
    texte: "Ton business organisé, tes stats, tes outils, au même endroit.",
    details: [
      "Un tableau de bord pour suivre tes ventes et celles de ton équipe",
      "Des diagnostics prêts à envoyer à tes prospects",
      "Un espace personnel pour organiser tes contacts et tes relances",
    ],
  },
  {
    titre: "Formation Réseaux Sociaux",
    texte: "Apprends à créer du contenu qui attire, sans jamais forcer.",
    details: [
      "Branding personnel : te positionner sans te déguiser",
      "Storytelling : raconter ton parcours pour créer l'identification",
      "Reels et Stories : les formats qui marchent, expliqués pas à pas",
      "Lives : structurer une prise de parole sans stress",
    ],
  },
  {
    titre: "START & CASH",
    texte: "8 vidéos pour tes premières ventes, sans tourner en rond.",
    details: [
      "8 modules courts pour aller droit à l'essentiel",
      "Pensé pour les toutes premières semaines dans l'équipe",
      "Objectif : ta première vente, sans te sentir perdue",
    ],
  },
  {
    titre: "Fast Start — 7 jours",
    texte: "Un programme progressif pour bien démarrer, jour après jour.",
    details: [
      "7 modules : bienvenue, produits, ton histoire, premiers contacts, présentation, ventes, équipe",
      "Une nouvelle étape débloquée chaque jour",
      "Ta marraine suit ta progression et t'accompagne en direct",
    ],
  },
  {
    titre: "Diagnostics Produits IA",
    texte: "Une intelligence artificielle qui crée une routine sur mesure pour chaque cliente.",
    details: [
      "5 questions, 2 minutes, résultat instantané",
      "Génère une vraie ordonnance produit personnalisée",
      "Renforce ton rôle de conseillère, pas de vendeuse",
    ],
  },
  {
    titre: "Éditorial IA",
    texte: "Un planning de contenu automatique, jamais à court d'idées.",
    details: [
      "2 posts et 3 stories générés chaque jour",
      "Hooks, légendes et thèmes prêts à personnaliser",
      "Planning structuré sur 4 semaines",
    ],
  },
  {
    titre: "La Roue de l'Équilibre",
    texte: "Un outil pour faire le point sur toi, avant de foncer.",
    details: [
      "Un diagnostic visuel de ton équilibre de vie actuel",
      "Sert de point de départ avant de se lancer dans le projet",
      "Disponible directement dans ton espace Mon Univers",
    ],
  },
  {
    titre: "Dream Board",
    texte: "Visualise tes objectifs pour les rendre concrets.",
    details: [
      "Un tableau de vision personnalisable",
      "Relie tes objectifs perso à ton activité",
      "Un rappel visuel de pourquoi tu as commencé",
    ],
  },
  {
    titre: "Carte de fidélité",
    texte: "Un système de récompenses pour fidéliser tes clientes sans effort.",
    details: [
      "Chaque commande rapproche la cliente d'un cadeau",
      "Suivi automatique, rien à calculer",
      "Renforce la relation sur le long terme",
    ],
  },
  {
    titre: "Communauté & entraide",
    texte: "Une équipe qui avance ensemble, jamais seule.",
    details: [
      "Challenges, défis et animations régulières",
      "Un espace pour poser tes questions à tout moment",
      "Des formations continues sur la vente et le recrutement",
    ],
  },
];

const contenuVide = {
  prenom: "",
  accroche: "",
  avantTexte: "",
  basculeEtapes: ["", "", "", ""],
  journee: [
    { heure: "8h", icon: "sunrise", texte: "" },
    { heure: "13h", icon: "sun", texte: "" },
    { heure: "20h", icon: "moon", texte: "" },
  ],
  motCle: "",
  lienDiagnostic: "/audit-blazing",
  lienInscription: "",
  temoignagesSelectionnes: [],
};

export default function ImmersionConfigTab({ uid, db, isChef, statsTrigger, userName }) {
  const [sousOnglet, setSousOnglet] = useState("contenu"); // contenu | temoignage | choix | commun
  const [contenu, setContenu] = useState(contenuVide);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    if (statsTrigger) setSousOnglet("stats");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsTrigger]);

  const [poolTemoignages, setPoolTemoignages] = useState([]);

  useEffect(() => {
    if (sousOnglet !== "stats") return;
    setLoadingStats(true);
    (async () => {
      try {
        const snap = await getDoc(doc(db, "immersion_stats", uid));
        setStats(snap.exists() ? snap.data() : {});
      } catch (e) {
        console.error("Erreur chargement stats immersion", e);
        setStats({});
      }
      setLoadingStats(false);
    })();
  }, [sousOnglet, uid]);
  const [loadingPool, setLoadingPool] = useState(false);
  const [savingChoix, setSavingChoix] = useState(false);
  const [savedChoix, setSavedChoix] = useState(false);

  const [enAttente, setEnAttente] = useState([]);
  const [loadingAttente, setLoadingAttente] = useState(false);
  const [traitementId, setTraitementId] = useState(null);

  const [communContenu, setCommunContenu] = useState({ outils: [] });
  const [savingCommun, setSavingCommun] = useState(false);
  const [savedCommun, setSavedCommun] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "immersion", uid));
        if (snap.exists()) {
          const data = snap.data();
          setContenu({ ...contenuVide, ...data, prenom: data.prenom || userName || "" });
        } else {
          setContenu({ ...contenuVide, prenom: userName || "" });
        }
      } catch (e) {
        console.error("Erreur chargement immersion", e);
      }
      setLoading(false);
    })();
  }, [uid]);

  useEffect(() => {
    if (!isChef) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "immersion", "_commun"));
        if (snap.exists() && (snap.data().outils || []).length > 0) {
          setCommunContenu({ outils: [], ...snap.data() });
        } else {
          setCommunContenu({ outils: OUTILS_DEFAUT_COMMUN });
        }
      } catch (e) {
        console.error("Erreur chargement contenu commun", e);
        setCommunContenu({ outils: OUTILS_DEFAUT_COMMUN });
      }
    })();
  }, [isChef]);

  useEffect(() => {
    if ((sousOnglet !== "choix" && sousOnglet !== "banque") || poolTemoignages.length > 0) return;
    setLoadingPool(true);
    (async () => {
      try {
        const q = query(collection(db, "temoignages"), where("valide", "==", true));
        const snap = await getDocs(q);
        const liste = [];
        snap.forEach((d) => liste.push({ id: d.id, ...d.data() }));
        liste.sort((a, b) => (a.profil || "").localeCompare(b.profil || ""));
        setPoolTemoignages(liste);
      } catch (e) {
        console.error("Erreur chargement pool témoignages", e);
      }
      setLoadingPool(false);
    })();
  }, [sousOnglet]);

  const toggleSelection = (id) => {
    const selectionnes = contenu.temoignagesSelectionnes || [];
    const next = selectionnes.includes(id) ? selectionnes.filter((x) => x !== id) : [...selectionnes, id];
    setContenu({ ...contenu, temoignagesSelectionnes: next });
  };

  const sauvegarderChoix = async () => {
    setSavingChoix(true);
    try {
      await setDoc(doc(db, "immersion", uid), { temoignagesSelectionnes: contenu.temoignagesSelectionnes || [] }, { merge: true });
      setSavedChoix(true);
      setTimeout(() => setSavedChoix(false), 2500);
    } catch (e) {
      console.error("Erreur sauvegarde sélection témoignages", e);
    }
    setSavingChoix(false);
  };

  const poolParCategorie = poolTemoignages.reduce((acc, t) => {
    const cat = t.profil || "Autre";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  useEffect(() => {
    if (!isChef) return;
    setLoadingAttente(true);
    (async () => {
      try {
        const q = query(collection(db, "temoignages"), where("valide", "==", false));
        const snap = await getDocs(q);
        const liste = [];
        snap.forEach((d) => liste.push({ id: d.id, ...d.data() }));
        setEnAttente(liste);
      } catch (e) {
        console.error("Erreur chargement temoignages en attente", e);
      }
      setLoadingAttente(false);
    })();
  }, [isChef, sousOnglet]);

  const validerTemoignage = async (id) => {
    setTraitementId(id);
    try {
      await updateDoc(doc(db, "temoignages", id), { valide: true });
      setEnAttente((liste) => liste.filter((t) => t.id !== id));
    } catch (e) {
      console.error("Erreur validation temoignage", e);
    }
    setTraitementId(null);
  };

  const supprimerTemoignage = async (id) => {
    setTraitementId(id);
    try {
      await deleteDoc(doc(db, "temoignages", id));
      setEnAttente((liste) => liste.filter((t) => t.id !== id));
    } catch (e) {
      console.error("Erreur suppression temoignage", e);
    }
    setTraitementId(null);
  };

  const sauvegarder = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "immersion", uid), contenu, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error("Erreur sauvegarde immersion", e);
    }
    setSaving(false);
  };

  const sauvegarderCommun = async () => {
    setSavingCommun(true);
    try {
      await setDoc(doc(db, "immersion", "_commun"), communContenu, { merge: true });
      setSavedCommun(true);
      setTimeout(() => setSavedCommun(false), 2500);
    } catch (e) {
      console.error("Erreur sauvegarde contenu commun", e);
    }
    setSavingCommun(false);
  };

  const majBascule = (i, val) => {
    const next = [...contenu.basculeEtapes];
    next[i] = val;
    setContenu({ ...contenu, basculeEtapes: next });
  };

  const majJournee = (i, val) => {
    const next = [...contenu.journee];
    next[i] = { ...next[i], texte: val };
    setContenu({ ...contenu, journee: next });
  };

  const majOutil = (i, field, val) => {
    const next = [...communContenu.outils];
    next[i] = { ...next[i], [field]: val };
    setCommunContenu({ ...communContenu, outils: next });
  };

  const ajouterOutil = () => {
    setCommunContenu({ ...communContenu, outils: [...(communContenu.outils || []), { titre: "", texte: "", details: [] }] });
  };

  const supprimerOutil = (i) => {
    setCommunContenu({ ...communContenu, outils: communContenu.outils.filter((_, idx) => idx !== i) });
  };

  const lienPreview = `${window.location.origin}/?immersion=${uid}`;

  const [copieLien, setCopieLien] = useState(false);
  const copierLien = () => {
    navigator.clipboard.writeText(lienPreview).then(() => {
      setCopieLien(true);
      setTimeout(() => setCopieLien(false), 2000);
    });
  };

  if (loading) return <div style={{ padding: 24, textAlign: "center" }}>Chargement...</div>;

  return (
    <div style={{ padding: "1rem", maxWidth: 560, margin: "0 auto" }}>
      <div id="decouverte-immersion-onglets" style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        <button onClick={() => setSousOnglet("contenu")} style={btnOnglet(sousOnglet === "contenu")}>
          Mon contenu
        </button>
        <button onClick={() => setSousOnglet("temoignage")} style={btnOnglet(sousOnglet === "temoignage")}>
          Mon témoignage
        </button>
        <button onClick={() => setSousOnglet("banque")} style={btnOnglet(sousOnglet === "banque")}>
          Banque de témoignages
        </button>
        <button onClick={() => setSousOnglet("choix")} style={btnOnglet(sousOnglet === "choix")}>
          Choisir mes témoignages
        </button>
        {isChef && (
          <button onClick={() => setSousOnglet("valider")} style={btnOnglet(sousOnglet === "valider")}>
            Valider les témoignages{enAttente.length > 0 ? ` (${enAttente.length})` : ""}
          </button>
        )}
        {isChef && (
          <button onClick={() => setSousOnglet("commun")} style={btnOnglet(sousOnglet === "commun")}>
            Contenu commun
          </button>
        )}
      </div>

      {sousOnglet === "contenu" && (
        <>
          <p style={{ fontSize: 13.5, opacity: 0.7, marginBottom: 20, lineHeight: 1.5 }}>
            Ce contenu alimente ta page d'immersion personnelle, à partager avec tes prospects.
          </p>

          <label style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: COLORS.brun, marginBottom: 6 }}>
            Ton lien à partager avec tes prospects
          </label>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              readOnly
              value={lienPreview}
              onFocus={(e) => e.target.select()}
              style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.15)", fontSize: 13, fontFamily: "inherit", background: "#f7f3ec" }}
            />
            <button
              onClick={copierLien}
              style={{ padding: "10px 16px", borderRadius: 10, border: "none", background: copieLien ? "#16a34a" : COLORS.or, color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              {copieLien ? "Copié ✓" : "Copier"}
            </button>
          </div>
          <a href={lienPreview} target="_blank" rel="noreferrer" id="decouverte-immersion-preview" style={{ display: "block", fontSize: 13, color: COLORS.or, fontWeight: 700, textDecoration: "none", marginBottom: 24 }}>
            Voir ma page d'immersion →
          </a>

          <Champ label="Ton prénom" value={contenu.prenom} onChange={(v) => setContenu({ ...contenu, prenom: v })} />
          <Champ label="Accroche (1ère phrase)" value={contenu.accroche} onChange={(v) => setContenu({ ...contenu, accroche: v })} textarea placeholder="Et si ta vie ressemblait enfin à ce que tu mérites ?" />
          <Champ label="Le Avant (identification)" value={contenu.avantTexte} onChange={(v) => setContenu({ ...contenu, avantTexte: v })} textarea placeholder="Il y a eu un temps où je me sentais invisible…" />

          <label style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: COLORS.brun, marginBottom: 6 }}>
            La bascule (4 étapes)
          </label>
          {contenu.basculeEtapes.map((etape, i) => (
            <Champ key={i} label={`Étape ${i + 1}`} value={etape} onChange={(v) => majBascule(i, v)} textarea placeholder={`Étape ${i + 1} de ton déclic`} />
          ))}

          <label style={{ display: "block", fontSize: 12.5, fontWeight: 700, color: COLORS.brun, marginBottom: 6 }}>
            Une journée type
          </label>
          {contenu.journee.map((mom, i) => (
            <Champ key={i} label={`${mom.heure}`} value={mom.texte} onChange={(v) => majJournee(i, v)} textarea placeholder="Ce que tu vis à ce moment de la journée" />
          ))}

          <Champ label="Mot-clé CTA" value={contenu.motCle} onChange={(v) => setContenu({ ...contenu, motCle: v })} placeholder="RENAISSANCE" />
          <Champ label="Lien vers ton diagnostic" value={contenu.lienDiagnostic} onChange={(v) => setContenu({ ...contenu, lienDiagnostic: v })} placeholder="/audit-blazing" />
          <Champ label="Lien d'inscription" value={contenu.lienInscription} onChange={(v) => setContenu({ ...contenu, lienInscription: v })} placeholder="https://blazingdinasty.com/r/tonlien" />

          <button
            onClick={sauvegarder}
            disabled={saving}
            style={{ width: "100%", padding: 14, borderRadius: 999, border: "none", background: `linear-gradient(90deg,#E4CE97,${COLORS.or})`, color: COLORS.brun, fontWeight: 700, fontSize: 15, cursor: "pointer", marginBottom: 12 }}
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
          {saved && <p style={{ color: "#16a34a", fontSize: 13.5, textAlign: "center", marginBottom: 16 }}>Enregistré ✓</p>}
        </>
      )}

      {sousOnglet === "temoignage" && (
        <TemoignagesEspace distributriceId={uid} distributriceNom={contenu.prenom || userName} db={db} />
      )}

      {sousOnglet === "banque" && (
        <>
          <p style={{ fontSize: 13.5, opacity: 0.7, marginBottom: 20, lineHeight: 1.5 }}>
            Tous les témoignages validés de l'équipe, classés par catégorie. De quoi t'inspirer, ou simplement voir le travail de toutes.
          </p>
          {loadingPool && <p style={{ fontSize: 13.5, opacity: 0.6 }}>Chargement…</p>}
          {!loadingPool && poolTemoignages.length === 0 && (
            <p style={{ fontSize: 13.5, opacity: 0.6 }}>Aucun témoignage validé pour le moment.</p>
          )}
          {Object.entries(poolParCategorie).map(([cat, liste]) => (
            <div key={cat} style={{ marginBottom: 26 }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: COLORS.or, marginBottom: 10 }}>
                {cat}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {liste.map((t) => (
                  <div
                    key={t.id}
                    style={{
                      borderRadius: 16,
                      overflow: "hidden",
                      border: "1px solid rgba(0,0,0,0.1)",
                      background: "#fff",
                    }}
                  >
                    {t.media?.type === "photo" && t.media?.url && (
                      <img src={t.media.url} alt="" style={{ width: "100%", maxHeight: 260, objectFit: "cover", display: "block" }} />
                    )}
                    {t.media?.type === "video" && t.media?.url && (
                      <video src={t.media.url} controls style={{ width: "100%", maxHeight: 260, display: "block" }} />
                    )}
                    <div style={{ padding: 14 }}>
                      <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 4 }}>{t.nom}</div>
                      {(t.media?.caption || t.texte) && (
                        <div style={{ fontSize: 13.5, lineHeight: 1.5, opacity: 0.85 }}>{t.media?.caption || t.texte}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {sousOnglet === "choix" && (
        <>
          <p style={{ fontSize: 13.5, opacity: 0.7, marginBottom: 20, lineHeight: 1.5 }}>
            Coche les témoignages validés de l'équipe que tu veux afficher dans <strong>ta</strong> page d'immersion. Si tu n'en coches aucun, ta page affichera une sélection par défaut.
          </p>
          {loadingPool && <p style={{ fontSize: 13.5, opacity: 0.6 }}>Chargement des témoignages…</p>}
          {!loadingPool && poolTemoignages.length === 0 && (
            <p style={{ fontSize: 13.5, opacity: 0.6 }}>Aucun témoignage validé pour le moment.</p>
          )}
          {Object.entries(poolParCategorie).map(([cat, liste]) => (
            <div key={cat} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700, color: COLORS.or, marginBottom: 8 }}>
                {cat}
              </div>
              {liste.map((t) => {
                const checked = (contenu.temoignagesSelectionnes || []).includes(t.id);
                return (
                  <label
                    key={t.id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: "10px 12px",
                      borderRadius: 12,
                      border: `1px solid ${checked ? COLORS.or : "rgba(0,0,0,0.12)"}`,
                      background: checked ? "rgba(201,165,92,0.08)" : "transparent",
                      marginBottom: 8,
                      cursor: "pointer",
                    }}
                  >
                    <input type="checkbox" checked={checked} onChange={() => toggleSelection(t.id)} style={{ marginTop: 3, accentColor: COLORS.or }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13.5 }}>{t.nom}</div>
                      <div style={{ fontSize: 13, opacity: 0.75, lineHeight: 1.4 }}>{t.texte}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          ))}
          {poolTemoignages.length > 0 && (
            <button
              onClick={sauvegarderChoix}
              disabled={savingChoix}
              style={{ width: "100%", padding: 14, borderRadius: 999, border: "none", background: `linear-gradient(90deg,#E4CE97,${COLORS.or})`, color: COLORS.brun, fontWeight: 700, fontSize: 15, cursor: "pointer", marginTop: 8 }}
            >
              {savingChoix ? "Enregistrement…" : "Enregistrer ma sélection"}
            </button>
          )}
          {savedChoix && <p style={{ color: "#16a34a", fontSize: 13.5, textAlign: "center", marginTop: 10 }}>Enregistré ✓</p>}
        </>
      )}

      {sousOnglet === "valider" && isChef && (
        <>
          <p style={{ fontSize: 13.5, opacity: 0.7, marginBottom: 20, lineHeight: 1.5 }}>
            Ces témoignages ont été soumis par l'équipe et attendent ta validation avant d'apparaître dans les pages d'immersion.
          </p>
          {loadingAttente && <p style={{ fontSize: 13.5, opacity: 0.6 }}>Chargement…</p>}
          {!loadingAttente && enAttente.length === 0 && (
            <p style={{ fontSize: 13.5, opacity: 0.6 }}>Aucun témoignage en attente pour le moment.</p>
          )}
          {enAttente.map((t) => (
            <div
              key={t.id}
              style={{
                borderRadius: 14,
                padding: 16,
                marginBottom: 12,
                border: "1px solid rgba(0,0,0,0.1)",
              }}
            >
              <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em", color: COLORS.or, marginBottom: 6, fontWeight: 700 }}>
                {t.profil} · {t.nom}
              </div>
              {t.texte && <div style={{ fontSize: 14, lineHeight: 1.5, marginBottom: 10 }}>{t.texte}</div>}
              {t.media?.url && (
                <div style={{ fontSize: 13, opacity: 0.7, marginBottom: 10 }}>
                  {t.media.type === "photo" ? "📷" : "🎥"} {t.media.url}
                  {t.media.caption && <div style={{ marginTop: 4 }}>{t.media.caption}</div>}
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  disabled={traitementId === t.id}
                  onClick={() => validerTemoignage(t.id)}
                  style={{ flex: 1, padding: "8px 12px", borderRadius: 999, border: "none", background: "#16a34a", color: "white", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                >
                  {traitementId === t.id ? "…" : "Valider"}
                </button>
                <button
                  disabled={traitementId === t.id}
                  onClick={() => supprimerTemoignage(t.id)}
                  style={{ flex: 1, padding: "8px 12px", borderRadius: 999, border: "1px solid #dc2626", background: "none", color: "#dc2626", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {sousOnglet === "stats" && (
        <>
          <p style={{ fontSize: 13.5, opacity: 0.7, marginBottom: 20, lineHeight: 1.5 }}>
            Le nombre de fois où chaque étape de ta page a été vue, et les clics sur tes liens clés.
          </p>
          {loadingStats && <p style={{ fontSize: 13.5, opacity: 0.6 }}>Chargement…</p>}
          {!loadingStats && stats && (
            <>
              <div style={{ marginBottom: 24 }}>
                {[
                  { key: "accroche", label: "Accroche" },
                  { key: "avant", label: "Le Avant" },
                  { key: "bascule", label: "La bascule" },
                  { key: "journee", label: "Journée type" },
                  { key: "coulisses", label: "Les coulisses" },
                  { key: "temoignages", label: "Témoignages" },
                  { key: "resultat", label: "Étape finale" },
                ].map((etape, i, arr) => {
                  const val = stats[etape.key] || 0;
                  const premier = stats[arr[0].key] || 0;
                  const pct = premier > 0 ? Math.round((val / premier) * 100) : 0;
                  return (
                    <div key={etape.key} style={{ marginBottom: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 4 }}>
                        <span>{etape.label}</span>
                        <span style={{ fontWeight: 700 }}>{val}{i > 0 && premier > 0 ? ` (${pct}%)` : ""}</span>
                      </div>
                      <div style={{ height: 8, background: "rgba(0,0,0,0.08)", borderRadius: 6, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: COLORS.or, borderRadius: 6 }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 130, borderRadius: 14, padding: 16, background: "rgba(201,165,92,0.08)", textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.or }}>{stats.clic_diagnostic || 0}</div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Clics sur "Diagnostic"</div>
                </div>
                <div style={{ flex: 1, minWidth: 130, borderRadius: 14, padding: 16, background: "rgba(201,165,92,0.08)", textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.or }}>{stats.clic_inscription || 0}</div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Clics sur "Inscription"</div>
                </div>
                <div style={{ flex: 1, minWidth: 130, borderRadius: 14, padding: 16, background: "rgba(201,165,92,0.08)", textAlign: "center" }}>
                  <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.or }}>{stats.lead_soumis || 0}</div>
                  <div style={{ fontSize: 12, opacity: 0.7 }}>Coordonnées reçues</div>
                </div>
              </div>
            </>
          )}
          {!loadingStats && stats && Object.keys(stats).length === 0 && (
            <p style={{ fontSize: 13.5, opacity: 0.6 }}>Aucune visite enregistrée pour le moment. Partage ton lien pour commencer à voir des chiffres ici !</p>
          )}
        </>
      )}

      {sousOnglet === "commun" && isChef && (
        <>
          <p style={{ fontSize: 13.5, opacity: 0.7, marginBottom: 20, lineHeight: 1.5 }}>
            Ce contenu est commun à toute l'équipe : les outils et formations affichés dans "Les coulisses" du tunnel.
          </p>
          {(communContenu.outils || []).map((o, i) => (
            <div key={i} style={{ border: "1px solid rgba(0,0,0,0.1)", borderRadius: 14, padding: 14, marginBottom: 14 }}>
              <Champ label="Titre" value={o.titre} onChange={(v) => majOutil(i, "titre", v)} />
              <Champ label="Description courte" value={o.texte} onChange={(v) => majOutil(i, "texte", v)} textarea />
              <Champ
                label="Détails (un point par ligne)"
                value={(o.details || []).join("\n")}
                onChange={(v) => majOutil(i, "details", v.split("\n").filter(Boolean))}
                textarea
              />
              <button onClick={() => supprimerOutil(i)} style={{ background: "none", border: "none", color: "#dc2626", fontSize: 12.5, cursor: "pointer" }}>
                Supprimer cet outil
              </button>
            </div>
          ))}
          <button onClick={ajouterOutil} style={{ width: "100%", padding: 12, borderRadius: 10, border: `1px dashed ${COLORS.or}`, background: "none", color: COLORS.or, fontWeight: 700, cursor: "pointer", marginBottom: 20 }}>
            + Ajouter un outil / une formation
          </button>
          <button
            onClick={sauvegarderCommun}
            disabled={savingCommun}
            style={{ width: "100%", padding: 14, borderRadius: 999, border: "none", background: `linear-gradient(90deg,#E4CE97,${COLORS.or})`, color: COLORS.brun, fontWeight: 700, fontSize: 15, cursor: "pointer" }}
          >
            {savingCommun ? "Enregistrement…" : "Enregistrer le contenu commun"}
          </button>
          {savedCommun && <p style={{ color: "#16a34a", fontSize: 13.5, textAlign: "center", marginTop: 10 }}>Enregistré ✓</p>}
        </>
      )}
    </div>
  );
}

function btnOnglet(active) {
  return {
    padding: "8px 16px",
    borderRadius: 999,
    border: `1px solid ${active ? COLORS.or : "rgba(0,0,0,0.15)"}`,
    background: active ? COLORS.or : "transparent",
    color: active ? COLORS.brun : "inherit",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  };
}
